import axios from "axios";
import * as Sentry from "@sentry/vue";
import EventBus from "@/services/eventBus";
import router from "@/router";
import {useMainStore} from "@/store";
import ReferenceManager from "@/services/referenceManager";
import StorageManager from "@/services/storageManager";
import {Capacitor} from "@capacitor/core";

const client = axios.create({
    baseURL: 'https://' + import.meta.env.VITE_API_HOST + ':' + import.meta.env.VITE_API_PORT,
    timeout: 60 * 1000,
    headers: Capacitor.isNativePlatform() && import.meta.env.VITE_NATIVE_CLIENT_KEY
        ? { 'X-Client-Key': import.meta.env.VITE_NATIVE_CLIENT_KEY }
        : {}
});

// Cold-start hardening. On a fresh native launch the first outbound HTTPS call
// (DNS + TCP + TLS to the API host) can stall; with the 60s client timeout that
// turned into a full 60s hang on the very first request of the session. Instead
// of waiting out the long timeout once, transient failures (no response received:
// network error or timeout) are retried a few times with a SHORT per-attempt
// timeout so a stalled cold connection aborts fast and a fresh attempt can
// succeed. Requests that reach the server and return an HTTP status (even 4xx/5xx)
// are NOT retried here — those are real responses handled below.
const COLD_RETRY_ATTEMPTS = 3;
const COLD_RETRY_TIMEOUT_MS = 8 * 1000;

// True when the failure is "nothing came back" (no HTTP response): a network
// error or a client-side timeout. These are the cold-connection stalls worth
// retrying. A response with any status code is a real answer, not a stall.
const isNoResponseError = (error) => {
    return error?.response === undefined
        && (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK' || error?.request !== undefined);
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Issues the request with short-timeout retries for no-response stalls. The
// caller's own `timeout` (if any) is respected on the final attempt; retried
// attempts use the short cold-start timeout so they fail fast.
const requestWithRetry = async (path, options) => {
    let lastError;
    for (let attempt = 1; attempt <= COLD_RETRY_ATTEMPTS; attempt++) {
        const isLast = attempt === COLD_RETRY_ATTEMPTS;
        const attemptOptions = Object.assign({}, options, {
            // Short timeout on early attempts so a cold stall aborts quickly; on
            // the last attempt fall back to the caller/client default so a
            // genuinely slow-but-working request still has headroom.
            timeout: isLast ? (options?.timeout ?? 0) : COLD_RETRY_TIMEOUT_MS,
        });
        try {
            return await client(path, attemptOptions);
        } catch (error) {
            lastError = error;
            if (isLast || !isNoResponseError(error)) {
                throw error;
            }
            // Brief backoff before the next attempt.
            await sleep(250 * attempt);
        }
    }
    throw lastError;
};

client.interceptors.request.use((request) => {
    const xdebug_session = sessionStorage.getItem('xdebug_session');
    if (xdebug_session !== null) {
        if (! Object.prototype.hasOwnProperty.call(request, 'params')) {
            request.params = {};
        }
        request.params.XDEBUG_SESSION_START = xdebug_session;
    }
    if (useMainStore().token !== null) {
        request.headers.Authorization = 'Bearer ' + useMainStore().token;
    }
    return request;
});

let refreshPromise = null;

const silentRefresh = () => {
    if (refreshPromise !== null) {
        return refreshPromise;
    }

    const identifier = StorageManager.has('identifier') ? StorageManager.get('identifier') : null;
    if (identifier === null) {
        return Promise.reject();
    }

    refreshPromise = client('connect', {
        method: 'post',
        data: {identifier},
    }).then(({data}) => {
        useMainStore().SET_TOKEN(data.user.token);
        refreshPromise = null;
        return data;
    }).catch(error => {
        refreshPromise = null;
        return Promise.reject(error);
    });

    return refreshPromise;
};

const request = (path, options, authRequest = false) => {
    return requestWithRetry(path, options)
        .then(response => {
            if (Object.prototype.hasOwnProperty.call(response.headers, 'x-knockout-references')
                && response.headers['x-knockout-references'] !== null
                && response.headers['x-knockout-references'] !== useMainStore().references.id
            ) {
                ReferenceManager.get(response.headers['x-knockout-references'])
                    .then(() => {})
                    .catch(e => {
                        if (e.response === undefined || e.response.status !== 503) {
                            useMainStore().SET_REFERENCES_ERROR(true);
                        }
                    });
            }
            return Promise.resolve(response);
        }).catch(error => {
            // TODO: 503 on api/references WHEN NO REFERENCES SET
            if (isForbiddenError(error) && isVpnRequired(error)) {
                router.push({name: 'vpn-required'}).then(() => {});
            } else if (isForbiddenError(error)) {
                useMainStore().SET_BAN(error.response.data);
                router.push({name: 'banned'}).then(() => {});
            } else if (isMaintenanceError(error)) {
                useMainStore().SET_MAINTENANCE({
                    end: error.response.data,
                });
                router.push({name: 'maintenance'}).then(() => {});
            } else {
                if (! authRequest && isAuthError(error)) {
                    return silentRefresh().then(() => {
                        const retryOptions = Object.assign({}, options);
                        if (retryOptions.headers) {
                            retryOptions.headers = Object.assign({}, retryOptions.headers);
                        }
                        return client(path, retryOptions);
                    }).catch(() => {
                        router.push({name: 'dashboard'}).then(() => {
                            EventBus.emit('error-auth');
                        });
                        return Promise.reject(error);
                    });
                } else if ((authRequest && !isAuthError(error) && !isValidationError(error))
                    || (!authRequest && !isValidationError(error) && !isNotFoundError(error))
                ) {
                    // Report to Sentry so failures are visible, not just shown as
                    // a transient toast. This branch is where genuine problems
                    // land — including a no-response stall that exhausted the
                    // cold-start retries (error.response === undefined), which is
                    // exactly the class of failure that was previously invisible.
                    Sentry.captureException(error, {
                        tags: { layer: 'api', noResponse: isNoResponseError(error) },
                        extra: {
                            path,
                            method: options?.method ?? 'get',
                            code: error?.code,
                            status: error?.response?.status,
                        },
                    });
                    EventBus.emit('error-fatal', error);
                }
            }
            return Promise.reject(error);
        });
};

const isAuthError = (error) => {
    return error.response !== undefined && error.response.status === 401;
};

const isForbiddenError = (error) => {
    return error.response !== undefined && error.response.status === 403;
};

const isVpnRequired = (error) => {
    if (error.response === undefined || error.response.status !== 403) {
        return false;
    }
    const data = error.response.data;
    if (typeof data === 'string') {
        return data.includes('VPN');
    }
    if (typeof data === 'object' && data !== null && typeof data.message === 'string') {
        return data.message.includes('VPN');
    }
    return false;
};

const isValidationError = (error) => {
    return error.response !== undefined && error.response.status === 422;
};

const isNotFoundError = (error) => {
    return error.response !== undefined && error.response.status === 404;
};

const isMaintenanceError = (error) => {
    return error.response !== undefined && error.response.status === 503;
}

export default request;